import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { addDays, format, startOfToday, startOfWeek, subWeeks } from "date-fns";
import AdminService from "../../../services/admin.service";
import downloadAllySettlementPdf from "../../../utils/allySettlementPdf";
import { useGlobal } from "../../../context/global";
import { AllySettlement as AllySettlementData, AllySettlementRow, AllySettlementStatus } from "../../../types/common.types";

// Resumen del aliado para el rango elegido en el filtro (por fecha de TOUR): lo que David le manda
// cada lunes. Desde acá se descarga el PDF y se crea el pago pendiente de ingreso en Caja
// ("Ventas Aliados"); cuando ese pago se completa con abonos (típicamente desde la conciliación),
// los pasajeros quedan pagados solos ("Conf. pago" en Reservas).

const clp = (n: number) => `$${Math.round(n).toLocaleString('es-CL')}`
const ddmmyyyy = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

// "Semana del 7 al 13 de Septiembre 2026" (o "del 28 de Septiembre al 4 de Octubre 2026").
function rangeTitle(from: string, to: string) {
    const [fy, fm, fd] = from.split('-').map(Number)
    const [ty, tm, td] = to.split('-').map(Number)
    if (from === to) return `${fd} de ${MESES[fm - 1]} ${fy}`
    if (fy === ty && fm === tm) return `Semana del ${fd} al ${td} de ${MESES[tm - 1]} ${ty}`
    return `Del ${fd} de ${MESES[fm - 1]}${fy !== ty ? ` ${fy}` : ''} al ${td} de ${MESES[tm - 1]} ${ty}`
}

const STATUS_LABEL: Record<AllySettlementStatus, string> = {
    pendiente: 'Pendiente',
    en_pago: 'En pago pendiente',
    pagado: 'Pagado',
}

interface Props {
    id: string
    day: Date
    endDay?: Date
    setDay: Dispatch<SetStateAction<Date>>
    setEndDay: Dispatch<SetStateAction<Date | undefined>>
}

export default function AllySettlement({ id, day, endDay, setDay, setEndDay }: Props) {
    const { setIsLoading, setMessage } = useGlobal()
    const [data, setData] = useState<AllySettlementData>()
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const from = format(day, 'yyyy-MM-dd')
    const to = format(endDay || day, 'yyyy-MM-dd')

    const load = useCallback(async () => {
        setIsLoading(true)
        try {
            const res = await AdminService.getAllySettlement(id, from, to)
            const settlement: AllySettlementData = res.data
            setData(settlement)
            // Por defecto se cobra todo lo pendiente del rango; se puede desmarcar a alguien.
            setSelected(new Set(settlement.rows.filter(r => r.status === 'pendiente').map(r => r.reservation_id)))
        } catch (error) {
            console.log(error)
            setData(undefined)
        } finally {
            setIsLoading(false)
        }
    }, [id, from, to, setIsLoading])

    useEffect(() => { load() }, [load])

    // Lunes a domingo de la semana anterior: lo que se le manda al aliado cada lunes.
    function lastWeek() {
        const monday = startOfWeek(subWeeks(startOfToday(), 1), { weekStartsOn: 1 })
        setDay(monday)
        setEndDay(addDays(monday, 6))
    }

    function toggle(row: AllySettlementRow) {
        if (row.status !== 'pendiente') return
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(row.reservation_id)) next.delete(row.reservation_id)
            else next.add(row.reservation_id)
            return next
        })
    }

    const selectedRows = useMemo(
        () => (data?.rows || []).filter(r => selected.has(r.reservation_id)),
        [data, selected]
    )
    const selectedTotal = selectedRows.reduce((acc, r) => acc + r.amount_due, 0)

    async function createPendingPayment() {
        if (!data || !selectedRows.length) return
        const ok = window.confirm(
            `¿Crear el pago pendiente de ${data.ally.name} por ${clp(selectedTotal)} (${selectedRows.length} pasajero${selectedRows.length === 1 ? '' : 's'})?\n\n` +
            'Va a quedar en Caja → Pagos pendientes como ingreso "Ventas Aliados". Cuando los abonos completen el total, esos pasajeros quedan pagados.'
        )
        if (!ok) return
        setIsLoading(true)
        try {
            await AdminService.createAllyPendingPayment({
                ally_id: data.ally.id, reservation_ids: selectedRows.map(r => r.reservation_id), from, to,
            })
            setMessage({ type: 'success', content: 'Pago pendiente creado. Está en Caja → Pagos pendientes.' })
            await load()
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: typeof error?.response?.data === 'string' ? error.response.data : 'No se pudo crear el pago pendiente',
            })
        } finally {
            setIsLoading(false)
            setTimeout(() => setMessage(undefined), 6000)
        }
    }

    // Liquidación en PDF (utils/allySettlementPdf.ts): todos los pasajeros del rango con su estado,
    // y arriba total adeudado / pagado / para cerrar. Se puede descargar siempre, también con todo
    // pagado (sirve de comprobante para el aliado).
    function downloadPDF() {
        if (!data) return
        if (!data.rows.length) {
            alert('No hay pasajeros con tour en este rango.')
            return
        }
        downloadAllySettlementPdf(data)
    }

    if (!data) return null

    return (
        <Main>
            <div className="header">
                <h3>{data.ally.name} · {rangeTitle(data.from, data.to)}</h3>
                <button type="button" className="secondary" onClick={lastWeek}>Semana pasada</button>
            </div>

            <div className="totals">
                <div><span>Total</span><strong>{clp(data.totals.total)}</strong></div>
                <div className="pendiente"><span>Pendiente</span><strong>{clp(data.totals.pending)}</strong></div>
                <div className="en_pago"><span>En pago pendiente</span><strong>{clp(data.totals.in_payment)}</strong></div>
                <div className="pagado"><span>Pagado</span><strong>{clp(data.totals.paid)}</strong></div>
            </div>

            {data.rows.length === 0 ? (
                <p className="empty">No hay pasajeros de este aliado con tour en este rango.</p>
            ) : (
                <>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Pasajero</th>
                                    <th>Fecha tour</th>
                                    <th>Tour</th>
                                    <th>Costo</th>
                                    <th>Entrada</th>
                                    <th>Total</th>
                                    <th>A cobrar</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.map(r => (
                                    <tr key={r.reservation_id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                aria-label={`Incluir a ${r.passenger_name}`}
                                                disabled={r.status !== 'pendiente'}
                                                checked={selected.has(r.reservation_id)}
                                                onChange={() => toggle(r)}
                                            />
                                        </td>
                                        <td>{r.passenger_name}</td>
                                        <td>{ddmmyyyy(r.reservation_date)}</td>
                                        <td>{r.tour_id}</td>
                                        <td>{clp(r.price)}</td>
                                        <td>{clp(r.tickets)}</td>
                                        <td>{clp(r.total)}</td>
                                        <td>
                                            {clp(r.amount_due)}
                                            {r.paid_direct > 0 && <div className="note">pagó {clp(r.paid_direct)} en la agencia</div>}
                                        </td>
                                        <td><span className={`badge ${r.status}`}>{STATUS_LABEL[r.status]}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="actions">
                        <button type="button" className="secondary" onClick={downloadPDF}>Descargar PDF</button>
                        <button type="button" onClick={createPendingPayment} disabled={!selectedRows.length}>
                            {selectedRows.length
                                ? `Crear pago pendiente · ${selectedRows.length} pasajero${selectedRows.length === 1 ? '' : 's'} · ${clp(selectedTotal)}`
                                : 'Crear pago pendiente'}
                        </button>
                    </div>
                </>
            )}
        </Main>
    )
}

const Main = styled.section`
width: 100%;
margin: 10px 0 25px;
display: flex;
flex-direction: column;
gap: 15px;
.header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    h3 { font-weight: 500; }
}
.totals {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    > div {
        flex: 1 1 140px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 10px 12px;
        border-radius: 8px;
        background-color: ${props => props.theme.whiteBone};
        span { font-size: 12px; color: ${props => props.theme.gray}; }
        strong { font-size: 18px; font-weight: 600; }
        &.pendiente strong { color: ${props => props.theme.red}; }
        &.pagado strong { color: ${props => props.theme.green}; }
    }
}
.empty { color: ${props => props.theme.gray}; }
.table-wrapper {
    width: 100%;
    overflow-x: auto;
}
table {
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
    font-size: 13px;
    th {
        font-weight: 500;
        text-align: left;
        padding: 8px;
        border-bottom: 1px solid ${props => props.theme.gray};
    }
    td {
        padding: 8px;
        border-bottom: 1px solid ${props => props.theme.whiteBone};
        vertical-align: top;
    }
    .note { font-size: 11px; color: ${props => props.theme.gray}; }
}
.badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    white-space: nowrap;
    &.pendiente { background-color: #ffe3e3; color: #b42318; }
    &.en_pago { background-color: #fff4cc; color: #8a6100; }
    &.pagado { background-color: #dcf5d8; color: #1a7a0c; }
}
.actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
}
button {
    padding: 10px 18px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    background-color: ${props => props.theme.blue};
    color: #fff;
    &.secondary {
        background-color: transparent;
        color: ${props => props.theme.blue};
        border: 1px solid ${props => props.theme.blue};
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
`
